import { z } from "zod";
// ---------- Enums ----------
export const ProductStatus = z.enum(["draft", "active", "archived"]);
export const AllergenCodes = z.enum([
    "gluten", "dairy", "eggs", "soy", "nuts", "peanuts", "sesame",
    "fish", "crustaceans", "molluscs", "celery", "mustard", "lupin", "sulphites",
]);
export const DietaryTagCodes = z.enum([
    "vegan", "vegetarian", "halal", "kosher", "organic", "gluten_free",
]);
export const StorageTypeSchema = z.enum(["ambient", "chilled", "frozen"]);
export const ProductSpecFields = z.object({
    brand: z.string().max(120).nullable().optional(),
    origin_country: z.string().regex(/^[A-Z]{2}$/).nullable().optional(),
    allergens: z.array(AllergenCodes).default([]),
    dietary_tags: z.array(DietaryTagCodes).default([]),
    ingredients: z.string().max(10_000).nullable().optional(),
    spec_extra: z
        .record(z.string().min(1).max(80), z.string().max(500))
        .refine((r) => Object.keys(r).length <= 50, "max 50 entries")
        .default({}),
});
export const ProductStorageFields = z.object({
    storage_type: StorageTypeSchema.nullable().optional(),
    storage_temp_min_c: z.number().int().min(-50).max(60).nullable().optional(),
    storage_temp_max_c: z.number().int().min(-50).max(60).nullable().optional(),
    shelf_life_days_unopened: z.number().int().positive().nullable().optional(),
    shelf_life_days_opened: z.number().int().positive().nullable().optional(),
    storage_notes: z.string().max(2000).nullable().optional(),
});
// ---------- Request bodies (admin) ----------
export const CreateProductBody = z
    .object({
    slug: z.string().min(1).max(120).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be kebab-case"),
    title: z.string().min(1).max(200),
    description: z.string().max(5000).optional(),
    vat_category_code: z.string().min(1).max(50),
    status: ProductStatus.default("draft"),
    category_ids: z.array(z.string().uuid()).default([]),
    // Brand entity link (a product has one brand). The admin syncs the legacy
    // `brand` text from the chosen brand's title on write.
    brand_id: z.string().uuid().nullable().optional(),
    // Supplier entity link (procurement; admin-only — storefront DTOs never
    // expose it). NULL = WMart own stock, excluded from PO fan-out.
    supplier_id: z.string().uuid().nullable().optional(),
})
    .extend(ProductSpecFields.shape)
    .extend(ProductStorageFields.shape);
export const UpdateProductBody = CreateProductBody.omit({ category_ids: true })
    .partial()
    .extend({ status: ProductStatus.optional() });
export const ListProductsQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: ProductStatus.optional(),
    q: z.string().max(200).optional(), // free-text on title or sku
    category_id: z.uuid().optional(),
});
export const CreateVariantBody = z.object({
    // NOTE: `sku` is intentionally absent. The DB assigns a zero-padded sequence
    // value via a column DEFAULT (Phase A) on insert; SKU is display-only and
    // immutable, never an app-supplied input.
    title: z.string().min(1).max(200),
    base_price_minor: z.coerce.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    currency: z.string().length(3).default("GBP"),
    stock_quantity: z.coerce.number().int().min(0).default(0),
    weight_grams: z.coerce.number().int().min(0).optional(),
    dimensions_json: z
        .object({
        length_mm: z.number().int().min(0),
        width_mm: z.number().int().min(0),
        height_mm: z.number().int().min(0),
    })
        .optional(),
    pack_size: z.string().max(120).nullable().optional(),
    ean: z.string().regex(/^\d{8,14}$/).nullable().optional(),
});
export const UpdateVariantBody = CreateVariantBody.partial();
export const UpdateProductVariantBody = UpdateVariantBody.extend({
    min_order_quantity: z.coerce.number().int().min(1).optional(),
    order_increment: z.coerce.number().int().min(1).optional(),
});
export const CreateCategoryBody = z.object({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be kebab-case"),
    title: z.string().min(1).max(200),
    parent_id: z.uuid().nullable().optional(),
    position: z.coerce.number().int().min(0).default(0),
});
export const UpdateCategoryBody = z.object({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .optional(),
    title: z.string().min(1).max(200).optional(),
    parent_id: z.uuid().nullable().optional(),
    position: z.coerce.number().int().min(0).optional(),
});
export const BulkCategoryPositionsBody = z.object({
    items: z
        .array(z.object({
        id: z.uuid(),
        parent_id: z.uuid().nullable(),
        position: z.coerce.number().int().min(0),
    }))
        .min(1)
        .max(500),
});
// ---------- Brand request bodies (admin) ----------
export const CreateBrandBody = z.object({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be kebab-case"),
    title: z.string().min(1).max(120),
    position: z.coerce.number().int().min(0).default(0),
});
export const UpdateBrandBody = z.object({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
        .optional(),
    title: z.string().min(1).max(120).optional(),
    position: z.coerce.number().int().min(0).optional(),
});
/** Reassign every product on this brand to `into_id`, then soft-delete this brand. */
export const MergeBrandBody = z.object({
    into_id: z.string().uuid(),
});
export const CreateVolumeBreakBody = z.object({
    min_quantity: z.coerce.number().int().min(1),
    unit_price_minor: z.coerce
        .number()
        .int()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER),
    currency: z.string().length(3).default("GBP"),
});
export const UpdateVolumeBreakBody = z.object({
    min_quantity: z.coerce.number().int().min(1).optional(),
    unit_price_minor: z.coerce
        .number()
        .int()
        .min(0)
        .max(Number.MAX_SAFE_INTEGER)
        .optional(),
});
export const UpdateImageBody = z.object({
    position: z.coerce.number().int().min(0).optional(),
    alt: z.string().max(500).nullable().optional(),
    variant_id: z.uuid().nullable().optional(),
});
export const AssignCategoryBody = z.object({
    category_id: z.uuid(),
});
// ---------- Request queries (storefront) ----------
/**
 * Sort options for the storefront products list (mobile Filter/Sort).
 *   - relevance  (default): stable insertion order (created_at, id) ASC.
 *   - name_asc:             product title A→Z.
 *   - price_asc / price_desc: by representative (MIN non-deleted variant)
 *                            base price; products with no live variant sort last.
 *   - newest:               created_at DESC (most-recently added first).
 */
export const StorefrontProductSort = z.enum([
    "relevance",
    "name_asc",
    "price_asc",
    "price_desc",
    "newest",
]);
export const ListStorefrontProductsQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    // category_slug: filter to products in any of the given categories' subtrees
    // (multi-select). Accepts repeated params (?category_slug=a&category_slug=b),
    // a CSV (?category_slug=a,b), or a single value — all normalize to a slug
    // array, so existing single-value callers (shop nav, category landing page,
    // mobile browse) keep working unchanged.
    category_slug: z.preprocess((v) => {
        if (v == null)
            return undefined;
        const arr = Array.isArray(v) ? v : String(v).split(",");
        const out = arr.map((s) => String(s).trim()).filter(Boolean);
        return out.length > 0 ? out : undefined;
    }, z
        .array(z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "category_slug must be kebab-case"))
        .optional()),
    q: z.string().max(200).optional(),
    // ---- mobile Filter/Sort (WS-F) — all optional, backward-compatible ----
    sort: StorefrontProductSort.default("relevance"),
    // in_stock: products with at least one non-deleted variant in stock.
    // Coerced so the wire string "true"/"false"/"1"/"0" parses cleanly.
    in_stock: z
        .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
        .transform((v) => v === true || v === "true" || v === "1")
        .optional(),
    // brand: filter to products whose brand_id matches one of the given brand
    // slugs (multi-select). Accepts repeated params (?brand=a&brand=b), a CSV
    // (?brand=a,b), or a single value — all normalize to a slug array.
    brand: z
        .preprocess((v) => {
        if (v == null)
            return undefined;
        const arr = Array.isArray(v) ? v : String(v).split(",");
        const out = arr.map((s) => String(s).trim()).filter(Boolean);
        return out.length > 0 ? out : undefined;
    }, z
        .array(z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "brand must be kebab-case"))
        .optional()),
    // dietary: products whose dietary_tags array contains this code.
    dietary: DietaryTagCodes.optional(),
});
/**
 * Query for `GET /v1/storefront/products/:slug/related`. `limit` is the size of
 * the relevance-ranked pool the endpoint returns; the PDP shuffles it and shows
 * a few. Default 12, capped so a single related call stays cheap.
 */
export const RelatedProductsQuery = z.object({
    limit: z.coerce.number().int().min(1).max(24).default(12),
});
